import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import avatar from "../../assets/images/avatar.png"
import arrowDown from "../../assets/images/icons/arrow-down.svg"
import edit from "../../assets/images/icons/edit.svg"
// import flag from "../../assets/images/icons/flag.svg"
import blockIcon from "../../assets/images/icons/block.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import {
  useBlockUserMutation,
  useDeleteUserMutation,
  useLazyGetUsersQuery,
  useUnblockUserMutation,
} from "store/api/user/user.api"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import BlockAccessConfirmation from "modals/blockAccessConfirmation"
import { UncontrolledTooltip } from "reactstrap"
import { getAdminsStatus, getTableSortingHeaderIcon } from "helpers/common"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import toast from "react-hot-toast"
import copy from "../../assets/images/icons/copy.svg"
import { copyToClipboard } from "helpers/utils"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"

const defaultFilters = {
  status: "",
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const UsersTable = ({ HeaderComponent }) => {
  const [getResults, { isFetching }] = useLazyGetUsersQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const [isBlockToast, setIsBlockToast] = useState(false)
  const [onDelete, { isLoading: isDeleting }] = useDeleteUserMutation()
  const [onBlock, { isLoading: isBlocking }] = useBlockUserMutation()
  const [onUnBlock, { isLoading: isUnBlocking }] = useUnblockUserMutation()
  const history = useNavigate()
  const deleteRef = useRef()
  const blockRef = useRef()
  const { hasAnyPermission, hasPermission } = usePermission()
  //meta title
  document.title = "Users | Prachay"

  useEffect(() => {
    getList()
  }, [filters])

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  const onActionSuccess = () => {
    setIsToast(false)
    deleteRef.current = null

    setIsBlockToast(false)
    blockRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, " User Details"),
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            email={row?.original.email}
            name={row?.original.name}
          />
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, " Role"),
        id: "role",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original?.role?.name}</>,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, " User ID"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <div>{row?.original.unique_code}</div>,
      },
      {
        header: () => <div className="flex items-center gap-2">Date</div>,
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getAdminsStatus(row?.original.status)}
          </div>
        ),
      },
      {
        header: () => (
          <div className="flex items-center gap-2">Last Logged In</div>
        ),
        accessorKey: "formatted_last_loggedin_at",
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const editTooltip = `editTooltip-${row?.original.id}`
          const deleteTooltip = `deleteTooltip-${row?.original.id}`
          const blockTooltip = `blockTooltip-${row?.original.id}`
          const copyTooltip = `copyTooltip-${row?.original.id}`

          return (
            <div className="d-flex justify-content-center align-items-center gap-3">
              {hasPermission(PERMISSIONS.USER_UPDATE) && (
                <>
                  <div className="action-view">
                    <Link
                      to={`/user-management/users/edit/${row?.original.id}`}
                    >
                      <img src={edit} id={editTooltip} alt="" />
                      <UncontrolledTooltip placement="top" target={editTooltip}>
                        Edit
                      </UncontrolledTooltip>
                    </Link>
                  </div>

                  {row?.original?.token &&
                    row?.original.status === "LINK_SENT" && (
                      <div
                        className="action-delete"
                        onClick={() => {
                          const url = `${process.env.REACT_APP_PUBLIC_URL}/admin/verify-account/${row.original.token}`
                          if (url) {
                            copyToClipboard(url).then(() =>
                              toast.success("Link Copied")
                            )
                          }
                        }}
                        id={copyTooltip}
                      >
                        <img src={copy} alt="" />
                        <UncontrolledTooltip
                          placement="top"
                          target={copyTooltip}
                        >
                          Copy Link
                        </UncontrolledTooltip>
                      </div>
                    )}
                </>
              )}

              {row?.original.status !== "LINK_SENT" && (
                <>
                  {hasPermission(PERMISSIONS.USER_DELETE) && (
                    <div
                      className="action-delete"
                      onClick={() => {
                        deleteRef.current = row?.original
                        setIsToast(true)
                      }}
                    >
                      <img src={deleteIcon} id={deleteTooltip} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={deleteTooltip}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </div>
                  )}

                  <div
                    className="action-view"
                    onClick={() => {
                      blockRef.current = row?.original
                      setIsBlockToast(true)
                    }}
                  >
                    <img src={blockIcon} id={blockTooltip} alt="" />
                    <UncontrolledTooltip placement="top" target={blockTooltip}>
                      {row?.original.status === "BLOCKED" ? "Unblock" : "Block"}
                    </UncontrolledTooltip>
                  </div>
                </>
              )}
            </div>
          )
        },
      },
    ]

    const canAccess = hasAnyPermission([
      PERMISSIONS.USER_UPDATE,
      PERMISSIONS.USER_DELETE,
    ])
    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasAnyPermission])

  const FilterComponents = () => (
    <>
      <select
        className="form-select pageSize"
        style={{ maxWidth: "150px" }}
        value={filters.status}
        onChange={(e) => {
          setFilters((prev) => ({
            ...prev,
            status: String(e.target.value),
          }))
        }}
      >
        {[
          { label: "All", value: "" },
          { label: "Active", value: "ACTIVE" },
          { label: "Blocked", value: "BLOCKED" },
        ].map((item, index) => (
          <option key={`status_${index}`} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      {/* <Link to={"/users/create"} className="btn btn-secondary">
        <i className="mdi mdi-plus me-1"></i> Create User
      </Link> */}
    </>
  )

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        count={count}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        HeaderComponent={HeaderComponent}
        FilterComponents={FilterComponents}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {isToast && deleteRef && (
        <DeleteConfirmationModal
          open={isToast}
          id={deleteRef.current.id}
          setOpen={setIsToast}
          isLoading={isDeleting}
          onClose={() => {
            setIsToast(false)
            deleteRef.current = null
          }}
          title={"Delete User"}
          description="Are you sure you want to delete this user?"
          onDelete={onDelete}
          onSuccess={onActionSuccess}
        />
      )}

      {isBlockToast && blockRef && (
        <BlockAccessConfirmation
          open={isBlockToast}
          id={blockRef.current.id}
          setOpen={setIsBlockToast}
          isLoading={isBlocking || isUnBlocking}
          onClose={() => {
            setIsBlockToast(false)
            blockRef.current = null
          }}
          title={
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? "Unblock Login"
              : "Block Login"
          }
          description={`Are you sure you want to ${
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? "unblock"
              : "block"
          } login for this user?`}
          onBlock={
            blockRef.current && blockRef.current.status === "BLOCKED"
              ? onUnBlock
              : onBlock
          }
          onSuccess={onActionSuccess}
        />
      )}
    </>
  )
}

export default UsersTable
